import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { exercisesApi } from '@/services/api';

interface Question {
  question: string;
  type: 'MULTIPLE_CHOICE' | 'TRUE_FALSE' | 'FILL_BLANK' | 'ESSAY' | 'MULTIPLE_SELECT' | 'DRAG_DROP' | 'MATCHING' | 'HOTSPOT';
  points: number;
  order: number;
  explanation?: string;
  options: QuestionOption[];
  matchPairs?: Array<{ left: string; right: string }>;
  hotspotConfig?: { imageUrl: string; correctAreas: Array<{ x: number; y: number; width: number; height: number }> };
  fillBlankConfig?: {
    questionText: string;
    blanks: Array<{ correctAnswer: string; position: number }>;
  };
}

interface QuestionOption {
  text: string;
  isCorrect: boolean;
  order: number;
}

interface Course {
  id: string;
  title: string;
}

interface CreateExerciseDialogProps {
  isOpen: boolean;
  onClose: () => void;
  courses: Course[];
  editingExercise?: {
    id: string;
    title: string;
    description?: string;
    courseId: string;
    questions: Question[];
  };
}

const CreateExerciseDialog: React.FC<CreateExerciseDialogProps> = ({
  isOpen,
  onClose,
  courses,
  editingExercise
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [courseId, setCourseId] = useState('');
  const [order, setOrder] = useState(0);
  const [questions, setQuestions] = useState<Question[]>([]);

  // Initialize form when editing or reset when creating
  useEffect(() => {
    if (editingExercise) {
      setTitle(editingExercise.title);
      setDescription(editingExercise.description || '');
      setCourseId(editingExercise.courseId);
      // Transform questions to match the format expected by the form
      const transformedQuestions = editingExercise.questions.map((question: any) => {
        if (question.type === 'FILL_BLANK' && question.fillBlankConfig) {
          // Transform fillBlankConfig back to options format for the form
          const blanks = question.fillBlankConfig.blanks || [];
          const options = blanks.map((blank: any, index: number) => ({
            text: blank.correctAnswer,
            isCorrect: true,
            order: index
          }));

          return {
            ...question,
            question: question.fillBlankConfig.questionText || question.question,
            options: options
          };
        }
        return question;
      });
      setQuestions(transformedQuestions);
    } else {
      setTitle('');
      setDescription('');
      setCourseId('');
      setOrder(0);
      setQuestions([{
        question: '',
        type: 'MULTIPLE_CHOICE',
        points: 1,
        order: 0,
        explanation: '',
        options: [
          { text: '', isCorrect: false, order: 0 },
          { text: '', isCorrect: false, order: 1 }
        ]
      }]);
    }
  }, [editingExercise]);

  const queryClient = useQueryClient();

  const createExerciseMutation = useMutation(
    async (data: any) => {
      if (editingExercise) {
        const response = await exercisesApi.update(editingExercise.id, data);
        return response.data;
      } else {
        const response = await exercisesApi.create(data);
        return response.data;
      }
    },
    {
      onSuccess: () => {
        toast.success(editingExercise ? 'Ejercicio actualizado correctamente' : 'Ejercicio creado correctamente');
        queryClient.invalidateQueries(['exercises']);
        queryClient.invalidateQueries('exercise-stats');
        handleClose();
      },
      onError: (error: any) => {
        console.error('Error saving exercise:', error);
        const message = error.response?.data?.message || 'Error al guardar el ejercicio';
        toast.error(message);
      }
    }
  );

  const handleClose = () => {
    setTitle('');
    setDescription('');
    setCourseId('');
    setOrder(0);
    setQuestions([
      {
        question: '',
        type: 'MULTIPLE_CHOICE',
        points: 1,
        order: 0,
        explanation: '',
        options: [
          { text: '', isCorrect: false, order: 0 },
          { text: '', isCorrect: false, order: 1 }
        ]
      }
    ]);
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error('El título es requerido');
      return;
    }

    if (!courseId) {
      toast.error('Debe seleccionar un curso');
      return;
    }

    const validQuestions = questions.filter(q => q.question.trim());
    if (validQuestions.length === 0) {
      toast.error('Debe agregar al menos una pregunta válida');
      return;
    }

    // Validate questions with options
    for (const question of validQuestions) {
      if (question.type === 'MULTIPLE_CHOICE' || question.type === 'MULTIPLE_SELECT') {
        const validOptions = question.options.filter(opt => opt.text.trim());
        if (validOptions.length < 2) {
          toast.error('Las preguntas de opción múltiple deben tener al menos 2 opciones');
          return;
        }
        const correctOptions = validOptions.filter(opt => opt.isCorrect);
        if (correctOptions.length === 0) {
          toast.error('Debe marcar al menos una opción como correcta');
          return;
        }
        if (question.type === 'MULTIPLE_CHOICE' && correctOptions.length > 1) {
          toast.error('Las preguntas de opción múltiple simple solo pueden tener una respuesta correcta');
          return;
        }
      }
    }

    // Transform questions to proper format for backend
    const transformedQuestions = validQuestions.map(question => {
      if (question.type === 'FILL_BLANK' && question.options) {
        // Transform FILL_BLANK options to fillBlankConfig format
        const blanks = question.options
          .filter(opt => opt.text.trim())
          .map((opt, index) => ({
            correctAnswer: opt.text.trim(),
            position: index + 1
          }));

        return {
          ...question,
          fillBlankConfig: {
            questionText: question.question,
            blanks: blanks
          }
        };
      }
      return question;
    });

    createExerciseMutation.mutate({
      title: title.trim(),
      description: description.trim() || undefined,
      courseId,
      order,
      questions: transformedQuestions
    });
  };

  const addQuestion = () => {
    const newQuestion: Question = {
      question: '',
      type: 'MULTIPLE_CHOICE',
      points: 1,
      order: questions.length,
      explanation: '',
      options: [
        { text: '', isCorrect: false, order: 0 },
        { text: '', isCorrect: false, order: 1 }
      ]
    };

    // Inicializar propiedades específicas según el tipo
    if (newQuestion.type === 'MATCHING') {
      newQuestion.matchPairs = [
        { left: '', right: '' },
        { left: '', right: '' }
      ];
    }

    if (newQuestion.type === 'HOTSPOT') {
      newQuestion.hotspotConfig = {
        imageUrl: '',
        correctAreas: [
          { x: 50, y: 50, width: 100, height: 100 }
        ]
      };
    }

    if (newQuestion.type === 'DRAG_DROP') {
      newQuestion.options = [
        { text: 'Elemento 1', isCorrect: true, order: 0 },
        { text: 'Elemento 2', isCorrect: false, order: 1 }
      ];
    }

    setQuestions([...questions, newQuestion]);
  };

  const removeQuestion = (index: number) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const updateQuestion = (index: number, field: string, value: any) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], [field]: value };

    // Si se cambia el tipo de pregunta, inicializar propiedades específicas
    if (field === 'type') {
      const question = updated[index];

      if (value === 'MATCHING' && !question.matchPairs) {
        question.matchPairs = [
          { left: '', right: '' },
          { left: '', right: '' }
        ];
      }

      if (value === 'HOTSPOT' && !question.hotspotConfig) {
        question.hotspotConfig = {
          imageUrl: '',
          correctAreas: [
            { x: 50, y: 50, width: 100, height: 100 }
          ]
        };
      }

      if (value === 'DRAG_DROP') {
        question.options = [
          { text: 'Elemento 1', isCorrect: true, order: 0 },
          { text: 'Elemento 2', isCorrect: false, order: 1 }
        ];
      }

      if (value === 'MULTIPLE_CHOICE' || value === 'MULTIPLE_SELECT') {
        if (!question.options || question.options.length < 2) {
          question.options = [
            { text: '', isCorrect: false, order: 0 },
            { text: '', isCorrect: false, order: 1 }
          ];
        }
      }
    }

    setQuestions(updated);
  };

  const addOption = (questionIndex: number) => {
    const updated = [...questions];
    const question = updated[questionIndex];
    question.options = [
      ...question.options,
      { text: '', isCorrect: false, order: question.options.length }
    ];
    setQuestions(updated);
  };

  const removeOption = (questionIndex: number, optionIndex: number) => {
    const updated = [...questions];
    const question = updated[questionIndex];
    question.options = question.options.filter((_, i) => i !== optionIndex);
    setQuestions(updated);
  };

  const updateOption = (questionIndex: number, optionIndex: number, field: string, value: any) => {
    const updated = [...questions];
    const option = updated[questionIndex].options[optionIndex];
    updated[questionIndex].options[optionIndex] = { ...option, [field]: value };
    setQuestions(updated);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingExercise ? 'Editar Ejercicio' : 'Crear Ejercicio Independiente'}</DialogTitle>
          <DialogDescription>
            Crea un ejercicio de tipo QUIZ que aparecerá como una lección independiente
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="title">Título del Ejercicio *</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ej: Quiz sobre JavaScript"
                required
              />
            </div>

            <div>
              <Label htmlFor="course">Curso *</Label>
              <select
                id="course"
                value={courseId}
                onChange={(e) => setCourseId(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
                required
              >
                <option value="">Seleccionar curso</option>
                {courses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.title}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descripción opcional del ejercicio"
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="order">Orden</Label>
              <Input
                id="order"
                type="number"
                value={order}
                onChange={(e) => setOrder(parseInt(e.target.value) || 0)}
                min="0"
                placeholder="0"
              />
            </div>
          </div>

          {/* Questions */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Preguntas</h3>
              <Button type="button" onClick={addQuestion} variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Agregar Pregunta
              </Button>
            </div>

            {questions.map((question, questionIndex) => (
              <Card key={questionIndex} className="p-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline">Pregunta {questionIndex + 1}</Badge>
                    {questions.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeQuestion(questionIndex)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-5 w-5" />
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Pregunta *</Label>
                      <Textarea
                        value={question.question}
                        onChange={(e) => updateQuestion(questionIndex, 'question', e.target.value)}
                        placeholder="Escribe tu pregunta aquí..."
                        rows={2}
                      />
                    </div>

                    <div className="space-y-2">
                      <div>
                        <Label>Tipo de Pregunta</Label>
                        <select
                          value={question.type}
                          onChange={(e) => updateQuestion(questionIndex, 'type', e.target.value)}
                          className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground"
                        >
                          <option value="MULTIPLE_CHOICE">Opción Múltiple</option>
                          <option value="TRUE_FALSE">Verdadero/Falso</option>
                          <option value="FILL_BLANK">Llenar Espacio</option>
                          <option value="ESSAY">Ensayo</option>
                          <option value="MULTIPLE_SELECT">Selección Múltiple</option>
                          <option value="DRAG_DROP">Arrastrar y Soltar</option>
                          <option value="MATCHING">Relacionar</option>
                          <option value="HOTSPOT">Punto Caliente</option>
                        </select>
                      </div>

                      <div>
                        <Label>Puntos</Label>
                        <Input
                          type="number"
                          value={question.points}
                          onChange={(e) => updateQuestion(questionIndex, 'points', parseInt(e.target.value) || 1)}
                          min="1"
                        />
                      </div>
                    </div>
                  </div>

                  {(question.type === 'MULTIPLE_CHOICE' || question.type === 'MULTIPLE_SELECT') && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Opciones de Respuesta</Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => addOption(questionIndex)}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Agregar Opción
                        </Button>
                      </div>
                      {question.type === 'MULTIPLE_SELECT' && (
                        <p className="text-sm text-blue-600 bg-blue-50 p-2 rounded">
                          💡 Selección Múltiple: Puedes marcar varias opciones como correctas
                        </p>
                      )}

                      {question.options.map((option, optionIndex) => (
                        <div key={optionIndex} className="flex items-center gap-2">
                          <input
                            type={question.type === 'MULTIPLE_CHOICE' ? "radio" : "checkbox"}
                            checked={option.isCorrect}
                            onChange={(e) => {
                              if (question.type === 'MULTIPLE_CHOICE') {
                                // Para radio buttons, desmarcar otras opciones
                                const updatedQuestions = [...questions];
                                updatedQuestions[questionIndex].options.forEach((opt, idx) => {
                                  opt.isCorrect = idx === optionIndex;
                                });
                                setQuestions(updatedQuestions);
                              } else {
                                updateOption(questionIndex, optionIndex, 'isCorrect', e.target.checked);
                              }
                            }}
                            name={`question-${questionIndex}-correct`}
                            className="h-4 w-4"
                          />
                          <Input
                            value={option.text}
                            onChange={(e) => updateOption(questionIndex, optionIndex, 'text', e.target.value)}
                            placeholder={`Opción ${optionIndex + 1}`}
                            className="flex-1"
                          />
                          {question.options.length > 2 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeOption(questionIndex, optionIndex)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-5 w-5" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Verdadero/Falso - Funcionalidad Completa */}
                  {question.type === 'TRUE_FALSE' && (
                    <div className="space-y-4 border-2 border-dashed border-blue-300 rounded-xl p-6 bg-gradient-to-br from-blue-50 to-indigo-50">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm font-bold">✓</span>
                        </div>
                        <h4 className="text-lg font-semibold text-gray-800">Verdadero o Falso</h4>
                      </div>

                      <div className="space-y-4">
                        {/* Pregunta Verdadero/Falso */}
                        <div className="space-y-3">
                          <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            ❓ Pregunta Verdadero/Falso
                          </Label>
                          <Textarea
                            value={question.question}
                            onChange={(e) => updateQuestion(questionIndex, 'question', e.target.value)}
                            placeholder="Escribe tu pregunta aquí. El estudiante deberá responder Verdadero o Falso."
                            rows={3}
                            className="border-2 border-blue-200 focus:border-blue-400 resize-none"
                          />
                          <p className="text-sm text-blue-600">
                            💡 Esta pregunta automáticamente mostrará opciones "Verdadero" y "Falso" al estudiante.
                          </p>
                        </div>

                        {/* Selección de Respuesta Correcta */}
                        <div className="space-y-3">
                          <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            ✅ Respuesta Correcta
                          </Label>
                          <div className="grid grid-cols-2 gap-3">
                            <label className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                              question.options?.[0]?.isCorrect
                                ? 'border-green-500 bg-green-50'
                                : 'border-gray-200 hover:border-green-300'
                            }`}>
                              <input
                                type="radio"
                                name={`tf-correct-${questionIndex}`}
                                checked={question.options?.[0]?.isCorrect || false}
                                onChange={() => {
                                  const updatedQuestions = [...questions];
                                  updatedQuestions[questionIndex] = {
                                    ...updatedQuestions[questionIndex],
                                    options: [
                                      { text: 'Verdadero', isCorrect: true, order: 1 },
                                      { text: 'Falso', isCorrect: false, order: 2 }
                                    ]
                                  };
                                  setQuestions(updatedQuestions);
                                }}
                                className="h-4 w-4"
                              />
                              <span className="font-medium">Verdadero</span>
                            </label>

                            <label className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                              question.options?.[1]?.isCorrect
                                ? 'border-green-500 bg-green-50'
                                : 'border-gray-200 hover:border-green-300'
                            }`}>
                              <input
                                type="radio"
                                name={`tf-correct-${questionIndex}`}
                                checked={question.options?.[1]?.isCorrect || false}
                                onChange={() => {
                                  const updatedQuestions = [...questions];
                                  updatedQuestions[questionIndex] = {
                                    ...updatedQuestions[questionIndex],
                                    options: [
                                      { text: 'Verdadero', isCorrect: false, order: 1 },
                                      { text: 'Falso', isCorrect: true, order: 2 }
                                    ]
                                  };
                                  setQuestions(updatedQuestions);
                                }}
                                className="h-4 w-4"
                              />
                              <span className="font-medium">Falso</span>
                            </label>
                          </div>
                        </div>

                        {/* Explicación */}
                        <div className="space-y-3">
                          <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            💬 Explicación (Opcional)
                          </Label>
                          <Textarea
                            value={question.explanation || ''}
                            onChange={(e) => updateQuestion(questionIndex, 'explanation', e.target.value)}
                            placeholder="Explica por qué esta respuesta es correcta..."
                            rows={2}
                            className="border-2 border-gray-200 focus:border-blue-400 resize-none"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Llenar Espacio - Funcionalidad Completa */}
                  {question.type === 'FILL_BLANK' && (
                    <div className="space-y-4 border-2 border-dashed border-yellow-300 rounded-xl p-6 bg-gradient-to-br from-yellow-50 to-amber-50">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm font-bold">📝</span>
                        </div>
                        <h4 className="text-lg font-semibold text-gray-800">Llenar Espacio en Blanco</h4>
                      </div>

                      <div className="space-y-4">
                        {/* Texto con Espacios */}
                        <div className="space-y-3">
                          <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            📄 Texto con Espacios en Blanco
                          </Label>

                          <div className="relative">
                            <Textarea
                              value={question.question}
                              onChange={(e) => updateQuestion(questionIndex, 'question', e.target.value)}
                              placeholder="Escribe tu texto aquí. Usa _____ (5 guiones bajos) para indicar dónde deben ir las respuestas."
                              rows={4}
                              className="border-2 border-yellow-200 focus:border-yellow-400 resize-none"
                            />
                            <div className="absolute top-2 right-2">
                              <div className="bg-yellow-100 text-yellow-700 text-xs px-2 py-1 rounded">
                                Usa _____ para espacios
                              </div>
                            </div>
                          </div>

                          {/* Extractor de Espacios */}
                          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                            <Label className="text-sm font-medium text-yellow-800 mb-2 block">
                              🎯 Espacios Detectados:
                            </Label>
                            <div className="flex flex-wrap gap-2">
                              {question.question.match(/_{3,}/g)?.map((_, index) => (
                                <div key={index} className="bg-white border-2 border-yellow-300 rounded px-3 py-1 text-sm">
                                  Espacio {index + 1}
                                </div>
                              )) || (
                                <span className="text-yellow-600 text-sm italic">No se detectaron espacios. Usa _____ en el texto.</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Respuestas Correctas */}
                        <div className="space-y-3">
                          <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            ✅ Respuestas Correctas (una por espacio)
                          </Label>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {question.question.match(/_{3,}/g)?.map((_, index) => (
                              <div key={index} className="flex items-center gap-2 p-3 bg-white rounded-lg border-2 border-yellow-200">
                                <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center text-yellow-600 text-sm font-bold">
                                  {index + 1}
                                </div>
                                <Input
                                  value={question.options[index]?.text || ''}
                                  onChange={(e) => {
                                    const updatedQuestions = [...questions];
                                    if (!updatedQuestions[questionIndex].options) {
                                      updatedQuestions[questionIndex].options = [];
                                    }
                                    updatedQuestions[questionIndex].options[index] = {
                                      text: e.target.value,
                                      isCorrect: true,
                                      order: index
                                    };
                                    setQuestions(updatedQuestions);
                                  }}
                                  placeholder={`Respuesta ${index + 1}`}
                                  className="flex-1 border-2 border-yellow-200 focus:border-yellow-400"
                                />
                              </div>
                            )) || (
                              <p className="text-yellow-600 text-sm italic col-span-2">
                                Añade espacios en blanco al texto primero usando _____
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Vista Previsualización */}
                        {question.question && (
                          <div className="p-4 bg-gray-50 rounded-lg">
                            <Label className="text-sm font-medium text-gray-700 mb-2 block">
                              👁️ Vista Previa para Estudiantes:
                            </Label>
                            <div className="p-3 bg-white rounded border-2 border-gray-200">
                              {question.question.replace(/_{3,}/g, () => {
                                return `<span className="inline-block min-w-[100px] px-3 py-1 mx-1 border-2 border-dashed border-blue-400 rounded bg-blue-50 text-blue-600">_________</span>`;
                              })}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Instrucciones */}
                      <div className="bg-yellow-100 border border-yellow-200 rounded-lg p-4">
                        <p className="text-sm text-yellow-800">
                          📝 <strong>Llenar Espacio:</strong> Los estudiantes verán un texto con espacios en blanco que deberán completar.
                          Usa _____ (5 guiones bajos) en el texto para indicar dónde van los espacios. Cada espacio necesita una respuesta correcta.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Ensayo - Funcionalidad Completa */}
                  {question.type === 'ESSAY' && (
                    <div className="space-y-4 border-2 border-dashed border-indigo-300 rounded-xl p-6 bg-gradient-to-br from-indigo-50 to-purple-50">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm font-bold">📝</span>
                        </div>
                        <h4 className="text-lg font-semibold text-gray-800">Respuesta de Ensayo</h4>
                      </div>

                      <div className="space-y-4">
                        {/* Pregunta de Ensayo */}
                        <div className="space-y-3">
                          <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            ❓ Pregunta de Ensayo
                          </Label>
                          <Textarea
                            value={question.question}
                            onChange={(e) => updateQuestion(questionIndex, 'question', e.target.value)}
                            placeholder="Escribe la pregunta de ensayo aquí. Los estudiantes deberán responder con texto extenso."
                            rows={3}
                            className="border-2 border-indigo-200 focus:border-indigo-400 resize-none"
                          />
                        </div>

                        {/* Criterios de Evaluación */}
                        <div className="space-y-3">
                          <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            📋 Criterios de Evaluación
                          </Label>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="p-3 bg-indigo-50 rounded-lg border-2 border-indigo-200">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-4 h-4 bg-indigo-300 rounded-full"></div>
                                <span className="text-sm font-medium text-indigo-700">Longitud Mínima</span>
                              </div>
                              <Input
                                type="number"
                                value={question.options[0]?.text || '100'}
                                onChange={(e) => {
                                  const updatedQuestions = [...questions];
                                  if (!updatedQuestions[questionIndex].options) {
                                    updatedQuestions[questionIndex].options = [];
                                  }
                                  updatedQuestions[questionIndex].options[0] = {
                                    text: e.target.value,
                                    isCorrect: true,
                                    order: 0
                                  };
                                  setQuestions(updatedQuestions);
                                }}
                                placeholder="100"
                                className="w-full border-2 border-indigo-200 focus:border-indigo-400"
                              />
                              <span className="text-xs text-indigo-600">palabras</span>
                            </div>

                            <div className="p-3 bg-purple-50 rounded-lg border-2 border-purple-200">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-4 h-4 bg-purple-300 rounded-full"></div>
                                <span className="text-sm font-medium text-purple-700">Puntuación Máxima</span>
                              </div>
                              <Input
                                type="number"
                                value={question.points || 10}
                                onChange={(e) => updateQuestion(questionIndex, 'points', parseInt(e.target.value) || 10)}
                                placeholder="10"
                                className="w-full border-2 border-purple-200 focus:border-purple-400"
                              />
                              <span className="text-xs text-purple-600">puntos</span>
                            </div>
                          </div>
                        </div>

                        {/* Palabras Clave */}
                        <div className="space-y-3">
                          <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            🔑 Palabras Clave para Guía
                          </Label>

                          <div className="space-y-2">
                            {question.options?.slice(1).map((option, index) => (
                              <div key={index + 1} className="flex items-center gap-2 p-2 bg-white rounded-lg border-2 border-gray-200">
                                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                <Input
                                  value={option.text}
                                  onChange={(e) => {
                                    const updatedQuestions = [...questions];
                                    if (!updatedQuestions[questionIndex].options) {
                                      updatedQuestions[questionIndex].options = [{ text: '100', isCorrect: true, order: 0 }];
                                    }
                                    updatedQuestions[questionIndex].options[index + 1] = {
                                      text: e.target.value,
                                      isCorrect: true,
                                      order: index + 1
                                    };
                                    setQuestions(updatedQuestions);
                                  }}
                                  placeholder={`Palabra clave ${index + 1}`}
                                  className="flex-1"
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const updatedQuestions = [...questions];
                                    if (updatedQuestions[questionIndex].options) {
                                      updatedQuestions[questionIndex].options.splice(index + 1, 1);
                                      setQuestions(updatedQuestions);
                                    }
                                  }}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-5 w-5" />
                                </Button>
                              </div>
                            )) || (
                              <p className="text-gray-500 text-sm italic">No hay palabras clave definidas</p>
                            )}

                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                const updatedQuestions = [...questions];
                                if (!updatedQuestions[questionIndex].options) {
                                  updatedQuestions[questionIndex].options = [{ text: '100', isCorrect: true, order: 0 }];
                                }
                                updatedQuestions[questionIndex].options.push({
                                  text: '',
                                  isCorrect: true,
                                  order: updatedQuestions[questionIndex].options.length
                                });
                                setQuestions(updatedQuestions);
                              }}
                              className="w-full border-2 border-dashed border-indigo-300 hover:border-indigo-400"
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Agregar Palabra Clave
                            </Button>
                          </div>
                        </div>

                        {/* Vista Previsualización */}
                        {question.question && (
                          <div className="p-4 bg-gray-50 rounded-lg">
                            <Label className="text-sm font-medium text-gray-700 mb-2 block">
                              👁️ Vista Previa para Estudiantes:
                            </Label>
                            <div className="p-3 bg-white rounded border-2 border-gray-200">
                              <div className="text-sm text-gray-700 mb-2 font-medium">{question.question}</div>
                              <div className="border-2 border-dashed border-gray-300 rounded p-4 text-center text-gray-500">
                                <div className="text-2xl mb-2">📝</div>
                                <div className="text-sm">Área de texto para respuesta extensa...</div>
                                <div className="text-xs text-gray-400 mt-1">
                                  Mínimo {question.options[0]?.text || 100} palabras
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Instrucciones */}
                      <div className="bg-indigo-100 border border-indigo-200 rounded-lg p-4">
                        <p className="text-sm text-indigo-800">
                          📝 <strong>Ensayo:</strong> Los estudiantes escribirán respuestas extensas que serán evaluadas manualmente.
                          Configura la longitud mínima, puntuación máxima y palabras clave guía para la evaluación.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Mensajes informativos para nuevos tipos de preguntas */}
                  {/* Arrastrar y Soltar - Funcionalidad Completa */}
                  {question.type === 'DRAG_DROP' && (
                    <div className="space-y-4 border-2 border-dashed border-purple-300 rounded-xl p-6 bg-gradient-to-br from-purple-50 to-pink-50">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm font-bold">🎯</span>
                        </div>
                        <h4 className="text-lg font-semibold text-gray-800">Arrastrar y Soltar</h4>
                      </div>

                      <div className="space-y-6">
                        {/* Elementos para Arrastrar */}
                        <div className="space-y-3">
                          <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            📦 Elementos para Arrastrar
                          </Label>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {question.options?.map((option, optionIndex) => (
                              <div key={optionIndex} className="flex items-center gap-2 p-3 bg-white rounded-lg border-2 border-purple-200 shadow-sm">
                                <div className="w-6 h-6 bg-purple-100 rounded flex items-center justify-center text-purple-600 text-sm font-bold">
                                  ☰
                                </div>
                                <Input
                                  value={option.text}
                                  onChange={(e) => updateOption(questionIndex, optionIndex, 'text', e.target.value)}
                                  placeholder={`Elemento ${optionIndex + 1}`}
                                  className="flex-1 border-2 border-purple-200 focus:border-purple-400"
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeOption(questionIndex, optionIndex)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-5 w-5" />
                                </Button>
                              </div>
                            )) || (
                              <p className="text-sm text-gray-500 italic col-span-2">No hay elementos definidos</p>
                            )}
                          </div>

                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => addOption(questionIndex)}
                            className="w-full border-2 border-dashed border-purple-300 hover:border-purple-400"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Agregar Elemento
                          </Button>
                        </div>

                        {/* Zonas de Destino */}
                        <div className="space-y-3">
                          <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            🎯 Zonas de Destino
                          </Label>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {question.options?.map((option, optionIndex) => (
                              <div key={optionIndex} className="p-3 bg-purple-50 border-2 border-dashed border-purple-300 rounded-lg">
                                <div className="flex items-center gap-2 mb-2">
                                  <div className="w-4 h-4 bg-purple-300 rounded"></div>
                                  <span className="text-sm font-medium text-purple-700">Zona {optionIndex + 1}</span>
                                  {option.isCorrect && (
                                    <Badge className="bg-green-100 text-green-700 border-green-200">
                                      Correcta
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-xs text-purple-600">
                                  Arrastra aquí: {option.text || `Elemento ${optionIndex + 1}`}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Vista Previsualización */}
                        {(question.options?.length || 0) > 0 && (
                          <div className="p-4 bg-gray-50 rounded-lg">
                            <Label className="text-sm font-medium text-gray-700 mb-3 block">
                              👁️ Vista Previa para Estudiantes:
                            </Label>
                            <div className="space-y-4">
                              <div className="p-3 bg-purple-100 rounded-lg">
                                <p className="text-sm font-medium text-purple-800 mb-2">📦 Elementos a Arrastrar:</p>
                                <div className="flex flex-wrap gap-2">
                                  {question.options?.map((option, index) => (
                                    <div key={index} className="px-3 py-2 bg-white rounded border-2 border-purple-300 text-sm cursor-move">
                                      {option.text || `Elemento ${index + 1}`}
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <div className="p-3 bg-blue-100 rounded-lg">
                                <p className="text-sm font-medium text-blue-800 mb-2">🎯 Zonas de Destino:</p>
                                <div className="grid grid-cols-2 gap-2">
                                  {question.options?.map((_, index) => (
                                    <div key={index} className="p-3 border-2 border-dashed border-blue-300 rounded text-center text-sm text-blue-600">
                                      Arrastra aquí
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Instrucciones */}
                      <div className="bg-purple-100 border border-purple-200 rounded-lg p-4">
                        <p className="text-sm text-purple-800">
                          🎯 <strong>Arrastrar y Soltar:</strong> Los estudiantes verán elementos que pueden arrastrar y soltar en zonas de destino.
                          Configura qué elementos son correctos y dónde deben ser colocados.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Relacionar - Funcionalidad Completa */}
                  {question.type === 'MATCHING' && (
                    <div className="space-y-4 border-2 border-dashed border-green-300 rounded-xl p-6 bg-gradient-to-br from-green-50 to-emerald-50">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm font-bold">🔗</span>
                        </div>
                        <h4 className="text-lg font-semibold text-gray-800">Relacionar Conceptos</h4>
                      </div>

                      <div className="space-y-4">
                        {/* Pares a Relacionar */}
                        <div className="space-y-3">
                          <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            🔗 Pares para Relacionar
                          </Label>

                          {question.matchPairs?.map((pair, pairIndex) => (
                            <div key={pairIndex} className="flex items-center gap-3 p-4 bg-white rounded-lg border-2 border-gray-200">
                              <div className="flex-1">
                                <Input
                                  value={pair.left}
                                  onChange={(e) => {
                                    const updatedQuestions = [...questions];
                                    const pairs = updatedQuestions[questionIndex].matchPairs || [];
                                    pairs[pairIndex] = { ...pairs[pairIndex], left: e.target.value };
                                    updatedQuestions[questionIndex].matchPairs = pairs;
                                    setQuestions(updatedQuestions);
                                  }}
                                  placeholder="Concepto izquierdo..."
                                  className="border-2 border-blue-200 focus:border-blue-400"
                                />
                              </div>
                              <div className="text-green-500 font-bold text-xl">↔️</div>
                              <div className="flex-1">
                                <Input
                                  value={pair.right}
                                  onChange={(e) => {
                                    const updatedQuestions = [...questions];
                                    const pairs = updatedQuestions[questionIndex].matchPairs || [];
                                    pairs[pairIndex] = { ...pairs[pairIndex], right: e.target.value };
                                    updatedQuestions[questionIndex].matchPairs = pairs;
                                    setQuestions(updatedQuestions);
                                  }}
                                  placeholder="Concepto derecho..."
                                  className="border-2 border-green-200 focus:border-green-400"
                                />
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const updatedQuestions = [...questions];
                                  const pairs = updatedQuestions[questionIndex].matchPairs || [];
                                  pairs.splice(pairIndex, 1);
                                  updatedQuestions[questionIndex].matchPairs = pairs;
                                  setQuestions(updatedQuestions);
                                }}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-5 w-5" />
                              </Button>
                            </div>
                          )) || (
                            <p className="text-sm text-gray-500 italic">No hay pares definidos</p>
                          )}

                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              const updatedQuestions = [...questions];
                              const newPair = { left: '', right: '' };
                              const pairs = updatedQuestions[questionIndex].matchPairs || [];
                              pairs.push(newPair);
                              updatedQuestions[questionIndex].matchPairs = pairs;
                              setQuestions(updatedQuestions);
                            }}
                            className="w-full border-2 border-dashed border-green-300 hover:border-green-400"
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Agregar Par
                          </Button>
                        </div>

                        {/* Vista Previsualización */}
                        {(question.matchPairs?.length || 0) > 0 && (
                          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                            <Label className="text-sm font-medium text-gray-700 mb-2 block">
                              👁️ Vista Previa para Estudiantes:
                            </Label>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <span className="text-xs font-medium text-gray-600">Columna Izquierda</span>
                                {question.matchPairs?.map((pair, index) => (
                                  <div key={index} className="p-2 bg-blue-100 rounded text-sm">
                                    {pair.left || `Elemento ${index + 1}`}
                                  </div>
                                ))}
                              </div>
                              <div className="space-y-2">
                                <span className="text-xs font-medium text-gray-600">Columna Derecha</span>
                                {question.matchPairs?.map((pair, index) => (
                                  <div key={index} className="p-2 bg-green-100 rounded text-sm">
                                    {pair.right || `Elemento ${index + 1}`}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Instrucciones */}
                      <div className="bg-green-100 border border-green-200 rounded-lg p-4">
                        <p className="text-sm text-green-800">
                          🔗 <strong>Relacionar:</strong> Los estudiantes verán dos columnas y deberán arrastrar los elementos de la izquierda para conectarlos con sus correspondientes de la derecha.
                          Cada par correcto sumará puntos.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Punto Caliente - Funcionalidad Completa */}
                  {question.type === 'HOTSPOT' && (
                    <div className="space-y-4 border-2 border-dashed border-blue-300 rounded-xl p-6 bg-gradient-to-br from-blue-50 to-indigo-50">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm font-bold">🎯</span>
                        </div>
                        <h4 className="text-lg font-semibold text-gray-800">Punto Caliente Interactivo</h4>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Subir Imagen */}
                        <div className="space-y-3">
                          <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            📷 Imagen para Punto Caliente
                          </Label>
                          <div className="relative">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  const reader = new FileReader();
                                  reader.onload = (event) => {
                                    const updatedQuestions = [...questions];
                                    updatedQuestions[questionIndex] = {
                                      ...updatedQuestions[questionIndex],
                                      hotspotConfig: {
                                        imageUrl: event.target?.result as string,
                                        correctAreas: updatedQuestions[questionIndex].hotspotConfig?.correctAreas || []
                                      }
                                    };
                                    setQuestions(updatedQuestions);
                                  };
                                  reader.readAsDataURL(file);
                                }
                              }}
                              className="hidden"
                              id={`hotspot-image-${questionIndex}`}
                            />
                            <label
                              htmlFor={`hotspot-image-${questionIndex}`}
                              className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-white hover:bg-gray-50 transition-colors"
                            >
                              {question.hotspotConfig?.imageUrl ? (
                                <div className="relative w-full h-full">
                                  <img
                                    src={question.hotspotConfig.imageUrl}
                                    alt="Hotspot"
                                    className="w-full h-full object-cover rounded-lg"
                                  />
                                  <div className="absolute inset-0 bg-black bg-opacity-20 rounded-lg flex items-center justify-center">
                                    <span className="text-white text-sm">Cambiar imagen</span>
                                  </div>
                                </div>
                              ) : (
                                <>
                                  <div className="text-gray-400 mb-2">
                                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                    </svg>
                                  </div>
                                  <span className="text-sm text-gray-600">Subir imagen</span>
                                </>
                              )}
                            </label>
                          </div>
                        </div>

                        {/* Áreas Correctas */}
                        <div className="space-y-3">
                          <Label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            ✅ Áreas Correctas
                          </Label>
                          <div className="space-y-2">
                            {question.hotspotConfig?.correctAreas?.map((area, areaIndex) => (
                              <div key={areaIndex} className="flex items-center gap-2 p-2 bg-white rounded border">
                                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                <span className="text-sm text-gray-600">
                                  Área {areaIndex + 1}: ({area.x}, {area.y}) - {area.width}×{area.height}
                                </span>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const updatedQuestions = [...questions];
                                    const areas = updatedQuestions[questionIndex].hotspotConfig?.correctAreas || [];
                                    areas.splice(areaIndex, 1);
                                    updatedQuestions[questionIndex].hotspotConfig = {
                                      ...updatedQuestions[questionIndex].hotspotConfig!,
                                      correctAreas: areas
                                    };
                                    setQuestions(updatedQuestions);
                                  }}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            )) || (
                              <p className="text-sm text-gray-500 italic">No hay áreas definidas</p>
                            )}

                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const updatedQuestions = [...questions];
                                const newArea = { x: 50, y: 50, width: 100, height: 100 };
                                const areas = updatedQuestions[questionIndex].hotspotConfig?.correctAreas || [];
                                areas.push(newArea);
                                updatedQuestions[questionIndex].hotspotConfig = {
                                  ...updatedQuestions[questionIndex].hotspotConfig!,
                                  correctAreas: areas
                                };
                                setQuestions(updatedQuestions);
                              }}
                              className="w-full"
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Agregar Área
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Instrucciones */}
                      <div className="bg-blue-100 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-800">
                          🎯 <strong>Punto Caliente:</strong> Los estudiantes deberán hacer clic en las áreas correctas de la imagen.
                          Cada área correcta definida sumará puntos. Sube una imagen y define las zonas donde los estudiantes deben hacer clic.
                        </p>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label>Explicación (Opcional)</Label>
                    <Textarea
                      value={question.explanation || ''}
                      onChange={(e) => updateQuestion(questionIndex, 'explanation', e.target.value)}
                      placeholder="Explicación de la respuesta correcta..."
                      rows={2}
                    />
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={createExerciseMutation.isLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {createExerciseMutation.isLoading ? (editingExercise ? 'Actualizando...' : 'Creando...') : (editingExercise ? 'Actualizar Ejercicio' : 'Crear Ejercicio')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateExerciseDialog;